import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useState, useCallback, useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { LeadCardData } from '../../../../types'
import { CampaignIndex } from '../../../../components/CampaignIndex'
import { useStageReveal } from '../../../../components/UniBox'
import { useAuth } from '../../../../contexts/AuthContext'
import { useCampaigns } from '../../../../hooks/useCampaigns'
import { useClientProfile } from '../../../../hooks/useClientProfile'
import { useCampaignCounts } from '../../../../hooks/useCampaignCounts'
import { useScrollRestore } from '../../../../hooks/useScrollRestore'
import { fetchLeadsFromN8N } from '../../../../utils/n8nDataFetchers'
import { queryKeys } from '../../../../services/query/queryKeys'
import { appwriteQueryFns } from '../../../../services/query/appwriteQueryFns'
import { getPreferredStatus } from '../../../../utils/statusMapping'
import BuyCreditsModal from '../../../../components/modals/BuyCreditsModal'
import InvoicesModal from '../../../../components/modals/InvoicesModal'
import { AppLayout } from '../../../../components/layouts/AppLayout'

type CampaignPageSearch = {
  view?: 'campaigns' | 'team'
  /** Return-from-Report-Generator restore params — re-open the ISR drawer
   *  for this lead (CampaignIndex consumes them). The router's default search
   *  parser JSON-parses numeric-looking ids to numbers, so accept both. */
  openLeadId?: string
  openCampaignId?: string
}

export const Route = createFileRoute('/campaigns/$campaignId/')({
  validateSearch: (search: Record<string, unknown>): CampaignPageSearch => ({
    view: search.view === 'team' ? 'team' : 'campaigns',
    openLeadId: (typeof search.openLeadId === 'string' || typeof search.openLeadId === 'number') && /^[\w-]{1,36}$/.test(String(search.openLeadId)) ? String(search.openLeadId) : undefined,
    openCampaignId: (typeof search.openCampaignId === 'string' || typeof search.openCampaignId === 'number') && /^[\w-]{1,36}$/.test(String(search.openCampaignId)) ? String(search.openCampaignId) : undefined,
  }),
  component: CampaignPage,
})

function CampaignPage() {
  "use memo";
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { campaignId } = Route.useParams()
  const { view, openLeadId, openCampaignId } = Route.useSearch()
  const { currentUser, targetEmail: impersonatedEmail, isTeamAdmin } = useAuth()

  // State
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false)
  const [isInvoicesModalOpen, setIsInvoicesModalOpen] = useState(false)
  const [isLoadingLead, setIsLoadingLead] = useState(false)

  // GSAP "liquid reveal" loading FX — fires on mount and on view switches.
  // Keyed on the campaign id so navigating between campaigns re-fires it.
  const stageRef = useStageReveal(campaignId, { revealOnFirstMount: true })

  // Use impersonated email if set (admin viewing as another user), otherwise current user's email
  const targetEmail = impersonatedEmail || currentUser?.email

  // TanStack Query hooks - campaigns is the single source of truth
  const clientQuery = useClientProfile(targetEmail)
  const campaignsQuery = useCampaigns(targetEmail)

  // Derive counts from campaigns data (no separate N8N call)
  const campaignCounts = useCampaignCounts(campaignsQuery.data)

  // Get the specific campaign's lead count from metrics
  const derivedLeadsCount = campaignCounts.campaigns[campaignId] ?? 0
  const derivedClientInfo = clientQuery.data
  const derivedAllCampaignIds = Object.keys(campaignCounts.campaigns)
  const viewMode: 'campaigns' | 'team' = view === 'team' ? 'team' : 'campaigns'

  const syncLeadStatusInCampaignCache = useCallback(
    (leadId: string, nextStatus: string | undefined) => {
      if (!nextStatus?.trim()) return

      const syncCacheKey = (cacheKey: readonly unknown[]) => {
        queryClient.setQueryData<LeadCardData[] | undefined>(cacheKey, (existingLeads) => {
          if (!existingLeads) return existingLeads

          return existingLeads.map((item) => {
            if (String(item.id) !== leadId) return item

            return {
              ...item,
              status: getPreferredStatus(item.status, nextStatus) || nextStatus,
            }
          })
        })
      }

      syncCacheKey(queryKeys.leads.byCampaign(campaignId))
      syncCacheKey(queryKeys.leads.byCampaign(campaignId, targetEmail))
    },
    [campaignId, queryClient, targetEmail]
  )

  // Gate only on shell essentials when we have NO data to show. Credits
  // (clientQuery) are intentionally excluded — get_credit_balance rides the
  // n8n-proxy, and self-hosted Appwrite's synchronous executions can
  // 408-timeout after 30s. Credits are header polish; the campaign view must
  // never wait on them (the header shows its own loading spinner).
  // True until the campaigns query has successfully resolved — so the skeleton
  // holds through the disabled → fetching → resolved window and never flashes
  // the "no campaigns" empty state before we know the result.
  const isLoadingCampaigns = !campaignsQuery.isSuccess

  // Handle lead selection - navigate to nested lead route
  // Pre-fetch lead data for cache, then navigate (the route will use cached data)
  const handleSelectLead = useCallback(
    async (lead: LeadCardData) => {
      if (!targetEmail) return
      syncLeadStatusInCampaignCache(String(lead.id), lead.status)
      navigate({
        to: '/campaigns/$campaignId/leads/$leadId',
        params: { campaignId, leadId: String(lead.id) },
      })

      // Keep navigation responsive while the route-level query warms the cache in the background.
      void queryClient.prefetchQuery({
        queryKey: queryKeys.leads.detail(campaignId, String(lead.id)),
        queryFn: async () => {
          const fullLead = await appwriteQueryFns.leads.detail({
            campaignId,
            leadId: String(lead.id),
            userEmail: targetEmail,
          })

          return {
            ...fullLead,
            status: getPreferredStatus(lead.status, fullLead?.status) || fullLead?.status,
          }
        },
        staleTime: 30 * 1000,
      }).catch((error) => {
        console.error('Error prefetching lead:', error)
      })
    },
    [navigate, campaignId, targetEmail, queryClient, syncLeadStatusInCampaignCache]
  )

  // Navigate back to all campaigns
  const handleNavigateHome = useCallback(() => {
    navigate({
      to: '/campaigns',
      search: { view: viewMode },
    })
  }, [navigate, viewMode])

  const hasNoCredits = !clientQuery.isPending && (derivedClientInfo?.credits ?? 0) <= 0

  // Restore scroll position on reconnect + data ready
  useScrollRestore(`campaign-${campaignId}`, !!derivedAllCampaignIds?.length)

  // Save the current campaign to sessionStorage so the campaigns list can prefetch it.
  useEffect(() => {
    try { sessionStorage.setItem('last-campaign-id', campaignId); } catch { /* noop */ }
  }, [campaignId])

  // Prefetch leads for this campaign immediately so CampaignIndex's
  // useLeadsForCampaign reads from cache instead of firing a new fetch.
  useEffect(() => {
    if (!targetEmail || !campaignId) return
    queryClient.prefetchQuery({
      queryKey: queryKeys.leads.byCampaign(campaignId, targetEmail),
      queryFn: () => fetchLeadsFromN8N(campaignId, targetEmail!),
      // This is a prefetch only — the mounted hook's own staleTime (2 min) governs
      // whether useLeadsForCampaign refetches; a short window here just avoids
      // re-firing the prefetch itself within it.
      staleTime: 30 * 1000,
    }).catch(() => { /* non-critical */ })
  }, [campaignId, targetEmail, queryClient])

  // Navigate to campaign creator
  const handleAddCampaign = useCallback(() => {
    if (hasNoCredits) {
      setIsBuyCreditsModalOpen(true)
      return
    }

    navigate({ to: '/campaigns/create/form' })
  }, [hasNoCredits, navigate])

  return (
    <AppLayout
      currentView="campaigns"
      onBuyCredits={() => setIsBuyCreditsModalOpen(true)}
      onViewInvoices={() => setIsInvoicesModalOpen(true)}
      aiCredits={derivedClientInfo?.credits ?? 0}
      creditsLoading={clientQuery.isPending}
      isBootLoading={isLoadingCampaigns && !campaignsQuery.data?.length}
      clientInfo={derivedClientInfo}
      isLoadingLead={isLoadingLead}
      isTeamAdmin={isTeamAdmin}
      teamMembersCount={campaignsQuery.teamMembers.length}
      onToggleTeamView={() => {
        navigate({
          to: '/campaigns',
          search: { view: viewMode === 'team' ? 'campaigns' : 'team' },
        })
      }}
      showTeamView={viewMode === 'team'}
    >
      {/* Main Content — renders immediately with CampaignIndex's skeletons
          while queries resolve; the GSAP liquid blur-in (keyed on campaignId)
          plays the entrance. No blocking overlay: campaign data comes from
          cache instantly on hard reload, and credits must never gate the
          view (n8n-proxy can 408-timeout for 30s on Appwrite). */}
      <main
        ref={stageRef}
        key={campaignId}
        className="app-page-shell app-page-offset max-w-[1600px] mx-auto px-6 py-4 pt-28"
        data-view-block
      >
        <CampaignIndex
          leadsCount={derivedLeadsCount}
          clientInfo={derivedClientInfo}
          availableCredits={derivedClientInfo?.credits ?? 0}
          onSelectLead={handleSelectLead}
          initialCampaignId={campaignId}
          allCampaignIds={derivedAllCampaignIds}
          campaigns={campaignsQuery.data}
          isLoadingCampaigns={isLoadingCampaigns}
          onNavigateHome={handleNavigateHome}
          onAddCampaign={handleAddCampaign}
          openISRLeadId={openLeadId}
          openISRCampaignId={openCampaignId}
        />
      </main>

      {/* Buy Credits Modal */}
      <BuyCreditsModal
        isOpen={isBuyCreditsModalOpen}
        onClose={() => setIsBuyCreditsModalOpen(false)}
        title={hasNoCredits ? 'Add Credits To Create A Campaign' : undefined}
        subtitle={
          hasNoCredits
            ? 'You need credits before starting a new campaign. Top up now and come back ready to launch without redoing the setup.'
            : undefined
        }
      />

      {/* Invoices Modal */}
      <InvoicesModal isOpen={isInvoicesModalOpen} onClose={() => setIsInvoicesModalOpen(false)} />
    </AppLayout>
  )
}
